/**
 * Repeating on a fixed plan, like a calendar app (Jens 2026-10-14/15). Pure date rules on calendar
 * days (y, m, d), no time zones — the same file is used by the server (supabase/functions/_shared)
 * and the app (src/utils); a test checks the two copies stay identical.
 *
 *   { kind: 'weekly', days: [4] }                                every Thursday
 *   { kind: 'weekly', days: [6], every: 4, from: '2026-10-17' }  every 4th week on Saturday
 *   { kind: 'every', every: 3, from: '2026-10-15' }              every 3rd day
 *   { kind: 'monthly', day: 15 }                                 the 15th of every month
 *   { kind: 'monthly', nth: 1, weekday: 7 }                      the first Sunday of every month
 *   { kind: 'monthly', every: 3, nth: 1, weekday: 6, from: … }   the first Saturday every 3rd month
 *   (days and weekday: 1 = Monday … 7 = Sunday; nth: 1–4, or -1 = the last)
 *
 * `from` (YYYY-MM-DD) is where counting starts for "every 2nd/3rd …"; a standard task leaves it
 * out and gets the day it is used (withStart).
 */
export type RepeatSchedule =
  | { kind: 'weekly'; days: number[]; every?: number; from?: string }
  | { kind: 'every'; every: number; from?: string }
  | { kind: 'monthly'; every?: number; day?: number; nth?: number; weekday?: number; from?: string };

const DAY_MS = 24 * 60 * 60 * 1000;
const isYmd = (v: unknown): v is string => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(v));
const int = (v: unknown, min: number, max: number): number | null => {
  const n = Number(v);
  return Number.isInteger(n) && n >= min && n <= max ? n : null;
};

/** A clean schedule from untrusted input, or null. */
export function parseSchedule(v: unknown): RepeatSchedule | null {
  if (!v || typeof v !== 'object') return null;
  const s = v as any;
  const from = isYmd(s.from) ? { from: s.from } : {};
  if (s.kind === 'weekly' && Array.isArray(s.days)) {
    const days = Array.from(new Set<number>(s.days.map(Number).filter((d: number) => Number.isInteger(d) && d >= 1 && d <= 7))).sort();
    if (!days.length) return null;
    const every = s.every == null ? 1 : int(s.every, 1, 52);
    if (every == null) return null;
    return every > 1 ? { kind: 'weekly', days, every, ...from } : { kind: 'weekly', days };
  }
  if (s.kind === 'every') {
    const every = int(s.every, 1, 365);
    return every == null ? null : { kind: 'every', every, ...from };
  }
  if (s.kind === 'monthly') {
    const every = s.every == null ? 1 : int(s.every, 1, 12);
    if (every == null) return null;
    const base = every > 1 ? { kind: 'monthly' as const, every, ...from } : { kind: 'monthly' as const };
    const day = s.day == null ? null : int(s.day, 1, 31);
    if (day != null) return { ...base, day };
    const nth = int(s.nth, -1, 4);
    const weekday = int(s.weekday, 1, 7);
    if (nth == null || nth === 0 || weekday == null) return null;
    return { ...base, nth, weekday };
  }
  return null;
}

/** "YYYY-MM-DD" for a calendar day. */
export const ymdOf = (y: number, m: number, d: number) => {
  const t = new Date(Date.UTC(y, m - 1, d));
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`;
};

/** A standard task's plan gets the day it is used as its start, so "every 4th week" counts from then. */
export function withStart(s: RepeatSchedule, today: string): RepeatSchedule {
  return 'from' in s && s.from ? s : { ...s, from: today };
}

/** Is this calendar day one of the planned days? */
export function isPlannedDay(s: RepeatSchedule, y: number, m: number, d: number): boolean {
  const utc = Date.UTC(y, m - 1, d);
  const date = new Date(utc);
  const iso = date.getUTCDay() === 0 ? 7 : date.getUTCDay();
  const fromUtc = s.from ? Date.parse(`${s.from}T00:00:00Z`) : null;
  if (fromUtc != null && utc < fromUtc) return false;

  if (s.kind === 'weekly') {
    if (!s.days.includes(iso)) return false;
    if (!s.every || s.every <= 1 || fromUtc == null) return true;
    // Weeks counted Monday to Monday from the start week.
    const fromDow = (new Date(fromUtc).getUTCDay() + 6) % 7;
    const weeks = Math.round(((utc - (iso - 1) * DAY_MS) - (fromUtc - fromDow * DAY_MS)) / (7 * DAY_MS));
    return weeks % s.every === 0;
  }
  if (s.kind === 'every') {
    if (fromUtc == null) return true;
    return Math.round((utc - fromUtc) / DAY_MS) % s.every === 0;
  }
  // monthly
  const realDay = date.getUTCDate();
  const month = date.getUTCMonth() + 1;
  const year = date.getUTCFullYear();
  if (s.every && s.every > 1 && fromUtc != null) {
    const f = new Date(fromUtc);
    const months = (year * 12 + month) - (f.getUTCFullYear() * 12 + f.getUTCMonth() + 1);
    if (months % s.every !== 0) return false;
  }
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (s.day != null) return realDay === Math.min(s.day, daysInMonth);
  if (iso !== s.weekday) return false;
  return s.nth === -1 ? realDay + 7 > daysInMonth : Math.ceil(realDay / 7) === s.nth;
}
